import { z } from 'zod';
export * from './combat.ts';
import {
  BasicCombatActionSchema, BasicCombatEventSchema, CombatPcCommandSchema, CombatPositionSchema,
  createCombatStateSchema,
} from './combat.ts';
import { StoryCommandSchema, StoryEventSchema, StoryViewSchema } from './story.ts';

export const VERSION = 1;
export const FACTIONS = ['SEN', 'BRZ', 'REE', 'SAL'] as const;
export const ZONES = ['Z01', 'Z02', 'Z03', 'Z04', 'Z05', 'Z06', 'Z07', 'Z08', 'Z09', 'Z10', 'Z11', 'Z12'] as const;
export const PROOFS = ['P-M1', 'P-M2', 'P-T1', 'P-T2', 'P-I1', 'P-I2'] as const;
export const ORIGINS = ['disgraced-legionary', 'unpaid-sellsword', 'fallen-household', 'intelligence-runner', 'escaped-worker', 'serekh-exile', 'hedge-binder', 'grave-witness'] as const;
export const ROLES = ['companion', 'leader', 'origin-relative', 'legacy', 'operator', 'witness', 'service'] as const;
const text = z.string().min(1);
const integer = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const id = (prefix: string) => z.string().regex(new RegExp(`^${prefix}:[a-zA-Z0-9._-]+$`));
export const PersonIdSchema = id('person');
export const RemainsIdSchema = id('remains');
export const ItemIdSchema = id('item');
export const RunIdSchema = id('run');
export const FactionSchema = z.enum(FACTIONS);
export const ZoneSchema = z.enum(ZONES);
export const ProofSchema = z.enum(PROOFS);
export const DigestSchema = z.string().regex(/^[0-9a-f]{16}$/);
export const Sha256Schema = z.string().regex(/^[0-9a-f]{64}$/);
export const NamespaceSchema = z.enum(['production', 'prototype', 'demo', 'test', 'debug']);
export const SourceRefSchema = z.strictObject({
  document: z.enum(['VISION.md', 'docs/design.md', 'docs/world/00-overview.md', 'docs/world/01-history.md', 'docs/world/02-factions.md', 'docs/world/03-geography.md', 'docs/world/04-zones.md', 'docs/world/05-pantheon.md', 'docs/world/06-threats.md', 'docs/world/07-cycle.md', 'docs/world/08-magic.md', 'docs/world/09-naming-and-tone.md']),
  section: text,
});
export const SourceIdSchema = z.enum([...ZONES, ...FACTIONS, ...PROOFS, 'LOC', 'F01', 'F02', 'F03', 'F04', 'T01', 'T02', 'T03', 'N01', 'N02', 'N03', 'R01', 'R02', 'R03', 'G01', 'G02', 'G03', 'G04', 'G05', 'G06', 'G07', 'G08', 'C01', 'C02', 'C03', 'B0', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'M0', 'M1', 'M2', 'M3', 'M4', 'M5']);
export const SeedEnvelopeSchema = z.strictObject({
  version: z.literal(VERSION), worldSeed: text, originId: z.enum(ORIGINS),
  continuityDigest: DigestSchema, chronologyId: id('chronology'), runOrdinal: integer.min(1),
  simulationVersion: z.literal('d00-v1'), contentManifestHash: Sha256Schema,
  rulesetId: z.enum(['d00-contract-bench', 'production-campaign']), namespace: NamespaceSchema,
});
export const AllocationSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('person'), id: PersonIdSchema }),
  z.strictObject({ kind: z.literal('remains'), id: RemainsIdSchema }),
  z.strictObject({ kind: z.literal('location'), id: text }),
  z.strictObject({ kind: z.literal('bequest'), id: id('chronology') }),
  z.strictObject({ kind: z.literal('consumed'), id: text }),
]);
export const ItemSchema = z.strictObject({
  id: ItemIdSchema, provenanceKey: text, name: text,
  kind: z.enum(['weapon', 'shield', 'worn', 'tool', 'ritual-dose', 'container', 'currency', 'quest-right']),
  units: integer.min(1), contents: z.array(ItemIdSchema), allocation: AllocationSchema,
});
export const SnapshotSchema = z.strictObject({
  name: text, origin: z.enum(ORIGINS), culture: text,
  originalRunId: RunIdSchema, latestRunId: RunIdSchema,
  equipment: z.array(ItemIdSchema), inventory: z.array(ItemIdSchema),
  skills: z.array(text), abilities: z.array(text), preparedRites: z.array(text), reagents: z.array(ItemIdSchema),
  hp: integer, maxHp: integer.min(1), allegiances: z.array(FactionSchema), oaths: z.array(text),
  reputation: z.array(text), personalOutcome: text, relationships: z.array(z.strictObject({ personId: PersonIdSchema, fact: text })),
  deeds: z.array(text), values: z.array(text), memories: z.array(text), evidence: z.array(ProofSchema),
});
export const PersonSchema = z.strictObject({
  id: PersonIdSchema, lineageKey: text, presentationKey: text, kinshipKeys: z.array(text),
  lifecycle: z.enum(['active', 'unmanifested', 'retired', 'dead']), location: ZoneSchema.nullable(),
  current: SnapshotSchema, original: SnapshotSchema.nullable(), deathEventId: text.nullable(),
});
export const RemainsSchema = z.strictObject({
  id: RemainsIdSchema, personId: PersonIdSchema,
  disposition: z.enum(['exposed', 'buried', 'animated', 'destroyed']), location: ZoneSchema,
  fixedBurial: z.boolean(),
});
export const VerificationActionSchema = z.strictObject({
  id: text, proofId: ProofSchema, routeId: text,
  method: z.enum(['measurement', 'comparison', 'direct-legacy']), witnessId: PersonIdSchema.nullable(),
});
export const ClosedRunSchema = z.strictObject({
  id: RunIdSchema, chronologyId: id('chronology'), ordinal: integer.min(1), namespace: NamespaceSchema,
  rulesetId: z.enum(['d00-contract-bench', 'production-campaign']),
  outcome: z.enum(['survived', 'died']), pcId: PersonIdSchema, actions: z.array(VerificationActionSchema),
});
export const ObservationSchema = z.strictObject({
  id: text, proofId: ProofSchema, runId: RunIdSchema, actionId: text, routeId: text,
});
export const DateSchema = z.strictObject({ day: integer.min(1).max(30), month: z.enum(['Sheaf', 'Press', 'Ember']), year: z.literal(641) });
export const ContinuitySchema = z.strictObject({
  version: z.literal(VERSION), chronologyId: id('chronology'), namespace: NamespaceSchema,
  closedRuns: z.array(ClosedRunSchema), persons: z.record(PersonIdSchema, PersonSchema),
  remains: z.record(RemainsIdSchema, RemainsSchema), items: z.record(ItemIdSchema, ItemSchema),
  legacyIds: z.array(PersonIdSchema), supportingIds: z.array(PersonIdSchema), observations: z.array(ObservationSchema),
  echoes: z.array(z.strictObject({ id: text, runId: RunIdSchema, immutableCore: text, source: SourceRefSchema })),
  pendingBequest: ItemIdSchema.nullable(), settlement: z.enum(['folded', 'settled']), date: DateSchema,
});
export const QuestRoleSchema = z.strictObject({
  id: text, role: z.enum(ROLES), preferredPersonId: PersonIdSchema.nullable(),
  fallback: z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('distinct-successor'), templateId: text, identifyingHistory: text }),
    z.strictObject({ kind: z.literal('document'), sourceId: SourceIdSchema, finding: text }),
    z.strictObject({ kind: z.literal('remains-goal'), finding: text }),
    z.strictObject({ kind: z.literal('exclude-hook'), reason: text }),
  ]),
});
export const RouteStepSchema = z.strictObject({
  id: text, label: text, requires: z.array(text), forbids: z.array(text), grants: z.array(text),
  cost: integer, witnessId: PersonIdSchema.nullable(), consentFact: text.nullable(),
  itemId: ItemIdSchema.nullable(), merchantFact: text.nullable(),
});
export const EvidenceOpportunitySchema = z.strictObject({
  id: text, proofId: ProofSchema, source: SourceRefSchema, initialFacts: z.array(text), budget: integer,
  goal: text, requiresLegacy: z.boolean(), steps: z.array(RouteStepSchema).min(1),
});
export const ContentCapsuleSchema = z.strictObject({
  version: z.literal(VERSION), id: text, sourceIds: z.array(SourceIdSchema).min(1),
  sources: z.array(SourceRefSchema).min(1), enabledMilestone: z.literal('D00'),
  roles: z.array(QuestRoleSchema), opportunities: z.array(EvidenceOpportunitySchema),
});
export const HoldingSchema = z.strictObject({ zoneId: ZoneSchema, holder: z.enum([...FACTIONS, 'LOC']), localAdministration: text.nullable() });
const pact = z.strictObject({ exchange: text, beneficiary: FactionSchema, expiryMilestone: z.enum(['B1', 'B2', 'B3', 'B4', 'B5', 'B6']) });
export const PoliticalStateSchema = z.strictObject({
  holdings: z.array(HoldingSchema).length(12), crisisHistory: z.enum(['strained-balance', 'military-protection', 'harbour-lease', 'contested-dues']),
  institutions: z.array(z.strictObject({ faction: FactionSchema, location: ZoneSchema, leverage: text, fighters: integer, hulls: integer.nullable(), leaderId: PersonIdSchema })),
  agreements: z.array(z.strictObject({ id: text, faction: FactionSchema, kind: z.enum(['transit', 'food', 'water', 'berth']), path: z.array(ZoneSchema).min(1), counterparties: z.array(z.enum([...FACTIONS, 'LOC'])), terms: pact })),
  relationships: z.array(z.strictObject({ a: FactionSchema, b: FactionSchema, stance: z.enum(['hostile', 'armed-truce', 'limited-pact']), obligation: pact.nullable() })).length(6),
  openingService: z.strictObject({ zoneId: z.literal('Z03'), name: text, accessible: z.literal(true) }),
  pressure: z.strictObject({ T01: integer.max(8), T02: integer.max(8), T03: integer.max(8) }),
});
export const PcIntentSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('pause'), paused: z.boolean() }),
  z.strictObject({ type: z.literal('wait'), ticks: integer.max(10000) }),
  z.strictObject({ type: z.literal('read'), interactionId: text }),
  z.strictObject({ type: z.literal('rest') }),
  z.strictObject({ type: z.literal('trade'), itemId: ItemIdSchema, sellerId: PersonIdSchema, price: integer }),
  z.strictObject({ type: z.literal('commit-milestone'), milestoneId: z.literal('bench:grain-agreement') }),
]);
export const AcceptedIntentSchema = z.strictObject({ tick: integer, sequence: integer, command: PcIntentSchema });
export const EventSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('milestone'), id: text, tick: integer, milestoneId: text }),
  z.strictObject({ type: z.literal('death'), id: text, tick: integer, personId: PersonIdSchema }),
  z.strictObject({ type: z.literal('abandoned-run'), id: text, tick: integer, reason: z.literal('pc-death') }),
  z.strictObject({ type: z.literal('trade'), id: text, tick: integer, itemId: ItemIdSchema }),
]);
export const RunStateSchema = z.strictObject({
  version: z.literal(VERSION), envelope: SeedEnvelopeSchema, frozenContinuity: ContinuitySchema,
  continuity: ContinuitySchema, pcId: PersonIdSchema, political: PoliticalStateSchema,
  tick: integer, lastSequence: z.number().int().min(-1), paused: z.boolean(), participation: z.enum(['active', 'ended']),
  money: integer, consumedEffects: z.array(text), events: z.array(EventSchema), intents: z.array(AcceptedIntentSchema),
  intermediateHashes: z.array(DigestSchema),
});
export const WorldViewSchema = z.strictObject({
  version: z.literal(VERSION), simulationHash: DigestSchema,
  entities: z.array(z.strictObject({ id: text, x: z.number().int(), y: z.number().int(), layer: integer, pose: text, appearance: text })),
  interactions: z.array(z.strictObject({ id: text, label: text, x: z.number().int(), y: z.number().int(), layer: integer })),
});
export const RecipeSchema = z.strictObject({
  version: z.literal(VERSION), id: text, sources: z.array(SourceRefSchema).min(1),
  provider: z.strictObject({ id: text, version: text }), prompt: text,
  seed: text, inputs: z.record(text, Sha256Schema),
  requirements: z.strictObject({ width: integer.min(1).max(4096), height: integer.min(1).max(4096), palette: z.array(z.string().regex(/^#[0-9a-f]{6}$/)).min(1), alpha: z.boolean() }),
  license: text, origin: text,
});
export const AssetManifestSchema = z.strictObject({
  version: z.literal(VERSION), recipeId: text, recipeHash: Sha256Schema,
  provider: z.strictObject({ id: text, version: text }), sources: z.array(SourceRefSchema).min(1),
  state: z.enum(['candidate', 'validated', 'promoted', 'rejected']),
  outputs: z.array(z.strictObject({ path: text, sha256: Sha256Schema, mediaType: text })).min(1),
  reviews: z.array(z.strictObject({ reviewer: text, passed: z.boolean(), detail: text })),
  license: text, origin: text,
});
export const SCHEMAS = { 'story-command': StoryCommandSchema, 'story-event': StoryEventSchema, 'story-view': StoryViewSchema, seed: SeedEnvelopeSchema, person: PersonSchema, remains: RemainsSchema, item: ItemSchema, continuity: ContinuitySchema, command: PcIntentSchema, event: EventSchema, run: RunStateSchema, capsule: ContentCapsuleSchema, projection: WorldViewSchema, 'asset-manifest': AssetManifestSchema, recipe: RecipeSchema, politics: PoliticalStateSchema };
export type SeedEnvelope = z.infer<typeof SeedEnvelopeSchema>;
export type Continuity = z.infer<typeof ContinuitySchema>;
export type Person = z.infer<typeof PersonSchema>;
export type Snapshot = z.infer<typeof SnapshotSchema>;
export type Item = z.infer<typeof ItemSchema>;
export type Allocation = z.infer<typeof AllocationSchema>;
export type PoliticalState = z.infer<typeof PoliticalStateSchema>;
export type Faction = z.infer<typeof FactionSchema>;
export type Zone = z.infer<typeof ZoneSchema>;
export type RunState = z.infer<typeof RunStateSchema>;
export type AcceptedIntent = z.infer<typeof AcceptedIntentSchema>;
export type WorldView = z.infer<typeof WorldViewSchema>;
export type Recipe = z.infer<typeof RecipeSchema>;
export type AssetManifest = z.infer<typeof AssetManifestSchema>;
export type EvidenceOpportunity = z.infer<typeof EvidenceOpportunitySchema>;
export type RouteStep = z.infer<typeof RouteStepSchema>;
export type ClosedRun = z.infer<typeof ClosedRunSchema>;
export type Observation = z.infer<typeof ObservationSchema>;
export type VerificationAction = z.infer<typeof VerificationActionSchema>;
export const jsonSchemas = (): Record<string, unknown> => Object.fromEntries(Object.entries(SCHEMAS).map(([name, schema]) => [name, z.toJSONSchema(schema)]));

export * from './story.ts';

const uint = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const positive = uint.min(1);
const coordinate = z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER);
export const PositionSchema = CombatPositionSchema;
export const NavigationSchema = z.strictObject({
  cellSize: positive,
  layers: z.array(z.strictObject({ id: uint, cells: z.array(z.union([z.strictObject({ x: coordinate, y: coordinate }), z.strictObject({ x: coordinate, y: coordinate, blocksSight: z.boolean() })])) })),
  links: z.array(z.strictObject({ from: PositionSchema, to: PositionSchema, cost: positive, bidirectional: z.boolean() })),
});
export const PlayActionSchema = BasicCombatActionSchema;
export const PlayCommandSchema = CombatPcCommandSchema;
const PlayCommandV1Schema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('pause'), paused: z.boolean() }),
  z.strictObject({ type: z.literal('queue'), action: PlayActionSchema }),
  z.strictObject({ type: z.literal('clear-queue') }),
]);
const acceptedPcV1 = z.strictObject({ tick: uint, sequence: uint, command: PlayCommandV1Schema });
const activeAction = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('move'), destination: PositionSchema, route: z.array(z.strictObject({ position: PositionSchema, cost: positive })).min(1), remainingTicks: positive }),
  z.strictObject({ type: z.literal('attack'), targetId: text, remainingTicks: positive }),
]);
const PlayCombatV1Schema = z.strictObject({
  tick: uint, paused: z.boolean(), pcId: PersonIdSchema,
  actors: z.record(PersonIdSchema, z.strictObject({
    id: PersonIdSchema, role: z.enum(['pc', 'companion', 'enemy']), position: PositionSchema,
    hp: uint, maxHp: positive, movePerTick: positive, attackDamage: positive,
    attackRange: positive, attackWindupTicks: positive, status: z.enum(['alive', 'dead']),
    activeAction: activeAction.nullable(),
  })),
  pcQueue: z.array(PlayActionSchema), lastPcSequence: z.number().int().min(-1),
  acceptedPcIntents: z.array(acceptedPcV1), events: z.array(BasicCombatEventSchema),
});
const goal = z.enum(['engage', 'guard', 'hold', 'withdraw']);
const trait = uint.max(10);
const companionObligationV1 = z.strictObject({ id: text, description: text, forbiddenGoals: z.array(goal) });
const PlayCompanionV1Schema = z.strictObject({
  companionId: PersonIdSchema, temperament: z.strictObject({ boldness: trait, loyalty: trait }),
  obligations: z.array(companionObligationV1),
  boundaries: z.array(z.discriminatedUnion('kind', [
    z.strictObject({ id: text, kind: z.literal('danger-limit'), dangerLimit: uint }),
    z.strictObject({ id: text, kind: z.literal('refuse-goal'), goal }),
    z.strictObject({ id: text, kind: z.literal('non-negotiable-topic'), topic: goal }),
  ])),
  preferences: z.strictObject({ engage: trait, guard: trait, hold: trait, withdraw: trait }),
  refusals: z.array(z.strictObject({ topic: text, reason: z.enum(['danger-exceeds-temperament', 'obligation-forbids', 'boundary-non-negotiable', 'temperament-declines', 'already-refused']), sequence: uint })),
  discussionSequence: uint,
});
export const PlayCompanionSchema = PlayCompanionV1Schema.extend({
  obligations: z.array(companionObligationV1.extend({ against: z.array(text).min(1).optional(), refusal: text.optional() })),
  agreedApproach: goal.optional(),
});
export const PlayCombatSchema = createCombatStateSchema(PersonIdSchema, ContinuitySchema, PlayCompanionSchema);
const PlaySceneV1Schema = z.strictObject({ zoneId: ZoneSchema, navigation: NavigationSchema, combat: PlayCombatV1Schema });
export const PlaySceneSchema = z.strictObject({ zoneId: ZoneSchema, navigation: NavigationSchema, combat: PlayCombatSchema });
export const PlayJournalEntrySchema = z.strictObject({ id: text, tick: uint, kind: z.enum(['event', 'death', 'evidence', 'legacy', 'closure']), text, sourceId: text.nullable() });

const playableShape = <Scene extends z.ZodType, Companion extends z.ZodType>(scene: Scene, companion: Companion) => ({
  format: z.literal('playable-run'),
  world: z.strictObject({ activeSceneId: text, scenes: z.record(text, scene) }),
  quests: z.strictObject({ facts: z.array(text), completedNodeIds: z.array(text) }),
  campaign: z.strictObject({
    milestones: z.array(text), outcomeId: text.nullable(),
    // Domain reducers validate these JSON values (kiln operation, rituals, economy, reports).
    systems: z.record(text, z.json()),
  }),
  companions: z.record(PersonIdSchema, companion),
  dialogue: z.strictObject({ speakerId: PersonIdSchema, nodeId: text, pausedBefore: z.boolean() }).nullable(),
  journal: z.array(PlayJournalEntrySchema),
  evidence: z.array(z.strictObject({ id: text, finding: text, sourceId: text, verified: z.boolean(), proofId: ProofSchema.nullable() })),
  verifications: z.array(VerificationActionSchema), observations: z.array(ObservationSchema),
  closure: z.strictObject({ outcome: z.enum(['survived', 'died']), bequestId: ItemIdSchema.nullable() }).nullable(),
});

/** Retained only to replay version-1 journals before migration. */
export const PlayableRunStateV1Schema = RunStateSchema.extend(playableShape(PlaySceneV1Schema, PlayCompanionV1Schema));

/** Version 2 retained only to verify and migrate older saves: its tactical ledger
 * must equal the life ledger before it is stripped. */
export const PlayableRunStateV2Schema = RunStateSchema.extend({
  version: z.literal(2),
  ...playableShape(PlaySceneSchema, PlayCompanionSchema),
});

/** One save boundary. Inventory, equipment, skills, oaths and allegiance live in continuity.
 * Version 3 persists without the duplicated tactical ledger. */
export const PlayableRunStateSchema = RunStateSchema.extend({
  version: z.literal(3),
  ...playableShape(PlaySceneSchema, PlayCompanionSchema),
});

/** Host requests are audited with their resulting state; they never address NPC orders. */
export const RunIntentSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('pc'), command: PlayCommandSchema }),
  z.strictObject({ type: z.literal('advance'), ticks: positive.max(10000) }),
  z.strictObject({ type: z.literal('interaction'), id: text, choiceId: text.nullable() }),
  z.strictObject({ type: z.literal('death'), personId: PersonIdSchema, eventId: text }),
  z.strictObject({ type: z.literal('close-life'), bequestId: ItemIdSchema.nullable() }),
  z.strictObject({ type: z.literal('new-life'), seed: text, originId: RunStateSchema.shape.envelope.shape.originId }),
]);
export const AcceptedRunIntentSchema = z.strictObject({ tick: uint, sequence: uint, runOrdinal: positive, command: RunIntentSchema });
export type PlayableRunStateV1 = z.infer<typeof PlayableRunStateV1Schema>;
export type PlayableRunStateV2 = z.infer<typeof PlayableRunStateV2Schema>;
export type PlayableRunState = z.infer<typeof PlayableRunStateSchema>;
export type PlayCompanion = z.infer<typeof PlayCompanionSchema>;
export type PlayScene = z.infer<typeof PlaySceneSchema>;
export type RunIntent = z.infer<typeof RunIntentSchema>;
export type AcceptedRunIntent = z.infer<typeof AcceptedRunIntentSchema>;

Object.assign(SCHEMAS, { 'playable-run': PlayableRunStateSchema, 'run-intent': RunIntentSchema });
